import { CalendarCheck, CalendarClock } from "lucide-react";
import type {
	ConstructionProgressResponse,
	SchedulePlannedSpan,
} from "@/adapters/api";
import { Progress } from "@/components/ui/progress.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import {
	formatGapPoints,
	formatPercent,
	formatShortDate,
} from "../../utils/format.ts";

const EMPTY_DATE = "A definir";

function comparison(data: ConstructionProgressResponse): string {
	if (data.no_baseline) {
		return "O cronograma da obra ainda não foi planejado.";
	}
	if (data.actual_percent >= data.planned_percent) {
		return "A obra está no prazo ou adiantada.";
	}
	return `Faltam ${formatGapPoints(data.planned_percent - data.actual_percent)} para alcançar o previsto para hoje.`;
}

export interface ClientHeroProps {
	name: string;
	data?: ConstructionProgressResponse;
	isPending: boolean;
	isError: boolean;
	span?: SchedulePlannedSpan;
	isSpanPending?: boolean;
}

export function ClientHero({
	name,
	data,
	isPending,
	isError,
	span,
	isSpanPending,
}: ClientHeroProps) {
	if (isPending) {
		return <Skeleton className="client-hero-skeleton" />;
	}

	if (isError || !data) {
		return (
			<p className="dashboard-panel-error" role="status">
				Não foi possível carregar o andamento da obra.
			</p>
		);
	}

	const start = isSpanPending
		? "—"
		: span?.planned_start_date
			? formatShortDate(span.planned_start_date)
			: EMPTY_DATE;
	const end = isSpanPending
		? "—"
		: span?.planned_end_date
			? formatShortDate(span.planned_end_date)
			: EMPTY_DATE;

	return (
		<div className="client-hero">
			<p className="client-hero__label">Andamento da obra</p>
			<h2 className="client-hero__name">{name}</h2>
			<p className="client-hero__value">{formatPercent(data.actual_percent)}</p>
			<Progress
				className="client-hero__bar"
				value={Math.min(100, Math.max(0, data.actual_percent))}
				aria-label="Avanço realizado da obra"
			/>
			<p className="client-hero__comparison">{comparison(data)}</p>
			<div className="client-hero__dates">
				<span className="client-hero__date">
					<CalendarClock className="client-hero__date-icon" />
					Início: {start}
				</span>
				<span className="client-hero__date">
					<CalendarCheck className="client-hero__date-icon" />
					Fim previsto: {end}
				</span>
			</div>
		</div>
	);
}
